import { Card } from "@/ingot";
import { useTranslation } from "react-i18next";

import { useTenantBranding } from "@/api/queries/branding";

const STEPS = ["upload", "configure", "order"] as const;

/**
 * Compact three-card row that summarises the customer flow on the
 * homepage. The detailed six-step alternating block lives on
 * `/how-it-works`; the homepage version is intentionally lighter so
 * the page doesn't repeat itself.
 *
 * Step 1's body interpolates the tenant's accepted upload formats
 * (from ``landing_facts.accepted_file_formats``) and per-tenant
 * max-upload-MB (from ``landing_facts.max_upload_mb``) so a
 * 3D-print-only tenant doesn't read "Přijímáme DXF, DWG a STEP" /
 * "20 MB".
 */
export function ThreeStepRow(): JSX.Element {
  const { t } = useTranslation("common");
  const branding = useTenantBranding();
  const facts = branding.data?.landing_facts ?? null;
  const formats = facts?.accepted_file_formats ?? [];
  const formatsList = formats.length > 0 ? formats.join(", ") : null;
  const maxMb = facts?.max_upload_mb ?? null;

  return (
    <div className="st-steps grid gap-4 md:grid-cols-3" data-testid="home-three-step">
      {STEPS.map((key, i) => (
        <Card
          key={key}
          padded
          elevation="raised"
          className="st-step flex flex-col gap-3"
        >
          <span className="st-num mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
            {String(i + 1).padStart(2, "0")}
          </span>
          <h3 className="text-lg font-semibold tracking-snug text-ink">
            {t(`home.threeStep.${key}.title`)}
          </h3>
          <p className="text-sm text-ink-2">
            {key === "upload" && formatsList && maxMb
              ? t("home.threeStep.upload.bodyDynamic", {
                  formats: formatsList,
                  maxMb,
                })
              : t(`home.threeStep.${key}.body`)}
          </p>
        </Card>
      ))}
    </div>
  );
}
